import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { huntNightLabel } from '../lib/huntNightLabel.js'
import { createLastHuntReader } from '../lib/lastHuntRead.js'
import { useSessionGeneration } from '../lib/useSessionGeneration.js'
import { worklistReach, reachTally, readHeaderLine, huntEvidenceReady, displayedHuntReach, REACH, recordHref } from '../lib/lastHunt.js'
import { teamFrom } from '../features/command-center/assignmentModel.js'
import ROSTER from '../data/sr_team_roster.json'
import { AgentBadge } from './AgentBadge.jsx'
import styles from './LastHunt.module.css'

/* ========================================================================= *
 * THE LAST HUNT, ON THE HEADER (Brian 2026-10-26).
 *
 * One line beside "Live · N championships": when the hunt last ran, what it
 * landed, when it runs next. Tap it and the Activity panel opens on that
 * run's crowns, each with its record, its source and the rep worklist it is
 * on, plus the last seven nights.
 *
 * READ-ONLY. The hunt facts come from `GET /api/hunt/last-run`; whether a
 * crown reached a rep is decided by the worklist's own ranking over the
 * universe this page already loaded (lib/lastHunt.js). Nothing here writes.
 *
 * NEVER A SILENT GREEN. A zero, an overdue run, a failed run, an unreadable
 * read or a crown on nobody's worklist all render in warning or alarm colour
 * with the reason in words.
 * ========================================================================= */

const ROSTER_NAMES = teamFrom(ROSTER).map((p) => p.name).filter(Boolean)
const REFRESH_MS = 5 * 60 * 1000

export function useLastHunt() {
  const generation = useSessionGeneration()
  const empty = { status: 'loading', report: null, error: null }
  const [snapshot, setSnapshot] = useState(() => ({generation, state: empty}))
  useEffect(() => {
    const reader = createLastHuntReader({
      read: options => api.huntLastRun(options),
      onState: state => setSnapshot({generation, state}),
    })
    reader.refresh()
    const timer = setInterval(() => reader.refresh(), REFRESH_MS)
    return () => { clearInterval(timer); reader.dispose() }
  }, [generation])
  return snapshot.generation === generation ? snapshot.state : empty
}

/** Reach for the crowns of the last landing, against the loaded universe. */
export function useLastHuntReach(report, universe, complete) {
  return useMemo(() => {
    const crowns = report?.lastLanding?.crowns || []
    if (!crowns.length) return []
    return worklistReach(crowns, universe || [], { roster: ROSTER_NAMES, complete: !!complete })
  }, [report, universe, complete])
}

const TONE = { ok: styles.ok, warn: styles.warn, alarm: styles.alarm, pending: styles.pending }

/** The header chip. A button: it opens the detail. */
export function LastHuntLine({ state, reach, onOpen }) {
  const h = readHeaderLine(state, reach)
  return (
    <button type="button" className={`${styles.line} ${TONE[h.tone] || styles.pending}`}
      onClick={onOpen} data-testid="cc-last-hunt" data-tone={h.tone} aria-label={`${h.text}. Open the last hunt.`}>
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.lineText}>{h.text}</span>
    </button>
  )
}

const host = (u) => { try { return new URL(u).host.replace(/^www\./, '') } catch { return '' } }
const etTime = (iso) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short', hour: 'numeric', minute: '2-digit' })
  } catch { return iso }
}
export function CrownRow({ c }) {
  const r = REACH[c.reach] || { tone: 'pending', words: c.reach }
  return (
    <li className={styles.crown}>
      <a className={styles.crownName} href={c.recordHref || recordHref(c.recordId)}>{c.recordName || `Record ${c.recordId}`}</a>
      <span className={styles.champion}>{c.champion || 'champion not named'}</span>
      <span className={styles.meta}>
        {c.sourceUrl
          ? <a href={c.sourceUrl} target="_blank" rel="noreferrer">source: {host(c.sourceUrl) || 'link'}</a>
          : <span className={styles.warnText}>no source on the write</span>}
        {c.via === 'split' && <span> · from a split</span>}
      </span>
      <span className={`${styles.reach} ${TONE[r.tone === 'alarm' ? 'alarm' : r.tone] || ''}`} data-reach={c.reach}>
        {r.words}{c.rep ? ` · ${c.rep}` : ''}{c.position ? ` · row ${c.position}` : ''}
      </span>
      {c.contactAction && (
        <span className={c.contactAction.live ? styles.meta : styles.warnText}>
          {c.contactAction.live ? `Next: ${c.contactAction.label}` : `Next action blocked: ${c.contactAction.label}`}
        </span>
      )}
      {c.worklistHref && (
        <a className={styles.worklistLink} href={c.worklistHref}>
          Open on {c.rep}&apos;s worklist
        </a>
      )}
    </li>
  )
}

/** Inside the Activity panel: the last hunt in full. */
export function LastHuntDetail({ state, reach = [] }) {
  if (state.status === 'loading' && !state.report) return <p className={styles.note}>Reading the last hunt...</p>
  if (!state.report) return <p className={styles.alarmText}>Could not read the last hunt: {state.error}. This is not an empty night.</p>
  const r = state.report
  const count = (source, key) => Number.isFinite(source?.[key]) && source[key] >= 0 ? source[key] : 'unknown'
  const v = r.verdict || {}
  const last = r.last
  const landing = r.lastLanding
  const t = reachTally(reach)
  const freshness = readHeaderLine(state, reach)
  const currentRead = state.status === 'ok' && huntEvidenceReady(r)
    && !reach.some((row) => ['board_not_loaded', 'board_not_refreshed'].includes(row.reach))
  const shownReach = displayedHuntReach(state, reach)
  return (
    <section className={styles.detail} aria-label="Last hunt" id="cc-last-hunt-detail">
      <p className={`${styles.headline} ${TONE[freshness.tone] || ''}`}>
        <AgentBadge agent={r.agent || 'unstamped'} compact /> {currentRead ? v.line : `Prior or incomplete report (${etTime(r.generatedAt)} ET). ${freshness.text}`}
      </p>
      {(v.reasons || []).length > 0 && (
        <ul className={styles.reasons}>{v.reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>
      )}
      {state.status === 'error' && <p className={styles.warnText}>The latest refresh failed ({state.error}); showing the read from {etTime(r.generatedAt)}.</p>}

      <p className={styles.note}>{r.scope?.kind === 'book' ? 'Current authorized book' : r.scope?.kind === 'company' ? 'Authorized company view' : 'Scope unconfirmed'}: counts describe only this read’s authorized scope, not all engine activity.</p>
      {last && (
        <p className={styles.note}>
          Last run {etTime(last.startedAt)} to {etTime(last.endedAt)} ET
          {last.idle ? ' · no writes reported in this scope' : ` · checked ${count(last.counts, 'checked')} · crowned ${count(last.counts, 'crownedTotal')} · not found ${count(last.counts, 'notFound')} · not yet ${count(last.counts, 'notYet')}${count(last.counts, 'split') ? ` · split ${count(last.counts, 'split')}` : ''}`}
          {last.note ? ` · "${last.note}"` : ''}
        </p>
      )}

      <h4 className={styles.sub}>
        {landing ? `Crowns from the ${etTime(landing.endedAt)} ET run (${count(landing.counts, 'crownedTotal')})` : 'Crowns'}
      </h4>
      {landing && currentRead && t.total > 0 && (
        <p className={t.flagged ? styles.warnText : styles.note}>
          {t.flagged
            ? `${t.flagged} of ${t.total} need worklist reach review. Each evidence gap is named below; a bundled-roster mismatch alone is not proof of no access.`
            : `${t.onWorklist} of ${t.total} are on their rep's worklist now${t.board_not_refreshed ? `; ${t.board_not_refreshed} appear on the next board read` : ''}.`}
        </p>
      )}
      {landing && currentRead && t.noContact > 0 && (
        <p className={styles.warnText}>
          {t.noContact} of them show no live call or email action on the worklist row, because the worklist has no contact loaded for them. The rep&apos;s live action today is opening the record.
        </p>
      )}
      {landing
        ? <ul className={styles.crowns}>{shownReach.map((c) => <CrownRow key={c.recordId} c={c} />)}</ul>
        : <p className={styles.warnText}>No landing report is available in this scoped read. That does not prove zero engine activity.</p>}

      <h4 className={styles.sub}>Recorded history ({r.history?.length || 0} nights)</h4>
      <ul className={styles.history}>
        {(r.history || []).map((h) => (
          <li key={h.night} className={h.crowned ? '' : styles.warnText}>
            <b>{huntNightLabel(h.night)}</b>{' '}
            {Number.isFinite(h.runs)
              ? h.runs > 0 ? `${h.runs} run${h.runs === 1 ? '' : 's'} · ${count(h, 'crowned')} crowned · ${count(h, 'checked')} checked · ${count(h, 'notFound')} not found${h.idleRuns ? ` · ${h.idleRuns} idle` : ''}` : 'no run recorded in this scope'
              : 'run count unknown for this scoped read'}
            {h.offWindowRuns ? ` · ${h.offWindowRuns} write burst${h.offWindowRuns === 1 ? '' : 's'} outside the window` : ''}
          </li>
        ))}
      </ul>
      <p className={styles.note}>
        {r.durable === true ? 'From the durable hunt ledger and scoped run evidence' : 'From a hunt report whose durable ledger evidence is unconfirmed'}; runs are {r.sources?.runIds || 'reconstructed'}.
        {r.sources?.heartbeatHistoryNote ? ` ${r.sources.heartbeatHistoryNote}` : ''}
        {r.sources?.ledger?.note ? ` ${r.sources.ledger.note}` : ''}
        {' '}{r.schedule?.verified === true ? 'Verified schedule' : 'Configured schedule (not verified live)'}: {r.schedule?.label || 'unknown'}. Read {etTime(r.generatedAt)} ET.
      </p>
    </section>
  )
}

export default LastHuntLine
